import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Group,
  MultiSelect,
  Select,
  Stack,
  Text,
} from '@mantine/core'
import { useLocation } from 'react-router-dom'
import { IconRefresh, IconStar } from '@/components/icons'
import {
  DataTable,
  DataTableScroll,
  DataTbody,
  DataTd,
  DataTh,
  DataThead,
  DataTr,
  EmptyState,
  PageCenterLoader,
  PageShell,
  PageToolbar,
  PaginationBar,
  dataTableClass,
} from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { api, ApiError } from '@/lib/api'
import { formatCreatedAtTableCell, formatWhen } from '@/lib/ticket-display'
import {
  mergeWithDefaultPermissions,
  hasPermission,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import { TICKETS_LIST_RETURN_URL_KEY } from '@/lib/open-ticket-navigation'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import type { MyPermissionsResponse, ProjectRow, ReviewListRow } from '@/types/api'

const REVIEWS_PAGE_SIZE_LS = 'reviews-page-size'
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

type ReviewFiltersState = {
  projectIds: string[]
  days: number
  rating: string
  requestedByAi: '' | 'true' | 'false'
}

const DEFAULT_FILTERS: ReviewFiltersState = {
  projectIds: [],
  days: 30,
  rating: '',
  requestedByAi: '',
}

function readSavedPageSize(): number {
  try {
    const raw = localStorage.getItem(REVIEWS_PAGE_SIZE_LS)
    const n = raw ? parseInt(raw, 10) : NaN
    if (PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return n
  } catch {
    /* ignore */
  }
  return 20
}

function savePageSize(n: number) {
  try {
    localStorage.setItem(REVIEWS_PAGE_SIZE_LS, String(n))
  } catch {
    /* ignore */
  }
}

function ratingCellText(r: ReviewListRow): string {
  const ratingStr = r.rating != null ? String(r.rating) : ''
  const labelStr = (r.rating_label != null ? String(r.rating_label).trim() : '') || ''
  const showLabel = !!labelStr && !!ratingStr && labelStr !== ratingStr
  if (!ratingStr) return '—'
  return showLabel ? `${ratingStr} (${labelStr})` : ratingStr
}

function requestedByLabel(r: ReviewListRow): string {
  if (r.requested_by_ai) return 'AI'
  const n = (r.requested_by_name || '').trim()
  return n || '—'
}

export function ReviewsPage() {
  const location = useLocation()
  const { user } = useAuth()
  const { openOrSelectTab } = useWorkspaceTabs()

  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])

  const effectivePerm = mergeWithDefaultPermissions(perm, user?.role)
  const canReviews = hasPermission(effectivePerm, 'access_reviews')

  const [projects, setProjects] = useState<ProjectRow[]>([])
  useEffect(() => {
    let alive = true
    void api
      .projects()
      .then((list) => {
        if (alive) setProjects(Array.isArray(list) ? list : [])
      })
      .catch(() => {
        if (alive) setProjects([])
      })
    return () => {
      alive = false
    }
  }, [])

  const [filterDraft, setFilterDraft] = useState<ReviewFiltersState>(DEFAULT_FILTERS)
  const [applied, setApplied] = useState<ReviewFiltersState>(DEFAULT_FILTERS)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(readSavedPageSize)

  const [rows, setRows] = useState<ReviewListRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadSeq = useRef(0)

  const openTicket = useCallback(
    (ticketId: string) => {
      try {
        sessionStorage.setItem(TICKETS_LIST_RETURN_URL_KEY, `${location.pathname}${location.search}`)
      } catch {
        /* ignore */
      }
      openOrSelectTab(`/tickets/${encodeURIComponent(ticketId)}`)
    },
    [location.pathname, location.search, openOrSelectTab],
  )

  const load = useCallback(async () => {
    if (!canReviews) {
      setRows([])
      setTotal(0)
      setLoading(false)
      return
    }
    const seq = ++loadSeq.current
    setLoading(true)
    setError(null)
    try {
      const offset = (page - 1) * pageSize
      const params: {
        project_id?: string
        days?: number
        rating?: number
        requested_by_ai?: boolean
        limit: number
        offset: number
      } = { limit: pageSize, offset }
      if (applied.projectIds.length) params.project_id = applied.projectIds.join(',')
      if (applied.days) params.days = applied.days
      if (applied.rating !== '') {
        const n = parseInt(applied.rating, 10)
        if (!Number.isNaN(n)) params.rating = n
      }
      if (applied.requestedByAi === 'true') params.requested_by_ai = true
      if (applied.requestedByAi === 'false') params.requested_by_ai = false

      const res = await api.reviewsList(params)
      if (seq !== loadSeq.current) return
      setRows(Array.isArray(res.items) ? res.items : [])
      setTotal(typeof res.total === 'number' && Number.isFinite(res.total) ? res.total : 0)
    } catch (e) {
      if (seq !== loadSeq.current) return
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить отзывы')
      setRows([])
      setTotal(0)
    } finally {
      if (seq === loadSeq.current) setLoading(false)
    }
  }, [
    applied.projectIds,
    applied.days,
    applied.rating,
    applied.requestedByAi,
    page,
    pageSize,
    canReviews,
  ])

  useEffect(() => {
    void load()
  }, [load])

  const applyFilters = useCallback(() => {
    setApplied({ ...filterDraft })
    setPage(1)
  }, [filterDraft])

  const onRefresh = useCallback(() => {
    void load()
  }, [load])

  const onPageSizeChange = useCallback((v: string | null) => {
    const n = parseInt(String(v), 10)
    if (!PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])) return
    savePageSize(n)
    setPageSize(n)
    setPage(1)
  }, [])

  const pages = Math.max(1, Math.ceil(total / pageSize))

  const projectSelectData = useMemo(() => {
    const opts = [{ value: '', label: 'Все проекты' }]
    for (const p of projects) {
      opts.push({ value: p.id, label: p.name || p.slug || p.id })
    }
    return opts
  }, [projects])

  if (!canReviews) {
    return (
      <PageShell>
        <Alert color="gray" variant="light" title="Нет доступа">
          У вас нет права просматривать оценки и отзывы.
        </Alert>
      </PageShell>
    )
  }

  return (
    <PageShell>
      <Stack gap="md">
        <PageToolbar
          leading={
            <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" wrap="nowrap">
                <IconStar size={20} stroke={1.6} />
                <Box>
                  <Text fw={700} size="lg">
                    Отзывы
                  </Text>
                  <Text size="sm" c="dimmed">
                    Оценки клиентов, комментарии и источники запроса обратной связи.
                  </Text>
                </Box>
              </Group>
              <Group gap="sm" wrap="wrap" align="flex-end">
                <Select
                  size="xs"
                  label="Период"
                  data={[
                    { value: '7', label: '7 дней' },
                    { value: '30', label: '30 дней' },
                    { value: '90', label: '90 дней' },
                  ]}
                  value={String(filterDraft.days)}
                  onChange={(value) => setFilterDraft((f) => ({ ...f, days: parseInt(value || '30', 10) || 30 }))}
                  allowDeselect={false}
                  checkIconPosition="right"
                  styles={{ root: { width: 130 } }}
                />
                <MultiSelect
                  size="xs"
                  label="Проект"
                  data={projectSelectData.filter((x) => x.value)}
                  value={filterDraft.projectIds}
                  onChange={(vals) => setFilterDraft((f) => ({ ...f, projectIds: vals }))}
                  maw={260}
                  searchable
                  clearable
                  placeholder="Все проекты"
                  nothingFoundMessage="Нет"
                />
                <Select
                  size="xs"
                  label="Оценка"
                  data={[
                    { value: '', label: 'Любая' },
                    { value: '1', label: '1' },
                    { value: '2', label: '2' },
                    { value: '3', label: '3' },
                    { value: '4', label: '4' },
                    { value: '5', label: '5' },
                    { value: '10', label: '10' },
                  ]}
                  value={filterDraft.rating}
                  onChange={(value) => setFilterDraft((f) => ({ ...f, rating: value || '' }))}
                  allowDeselect={false}
                  checkIconPosition="right"
                  styles={{ root: { width: 110 } }}
                />
                <Select
                  size="xs"
                  label="Кто запросил"
                  data={[
                    { value: '', label: 'Все' },
                    { value: 'true', label: 'AI' },
                    { value: 'false', label: 'Оператор' },
                  ]}
                  value={filterDraft.requestedByAi}
                  onChange={(value) =>
                    setFilterDraft((f) => ({
                      ...f,
                      requestedByAi: (value || '') as ReviewFiltersState['requestedByAi'],
                    }))
                  }
                  allowDeselect={false}
                  checkIconPosition="right"
                  styles={{ root: { width: 140 } }}
                />
                <Button size="xs" onClick={applyFilters}>
                  Применить
                </Button>
              </Group>
            </Stack>
          }
          trailing={
            <ActionIcon variant="default" size="md" aria-label="Обновить" title="Обновить" onClick={onRefresh}>
              <IconRefresh size={16} stroke={1.5} />
            </ActionIcon>
          }
        />

        {error ? (
          <Alert color="red" variant="light" title="Ошибка загрузки">
            {error}
          </Alert>
        ) : null}

        {loading && rows.length === 0 ? (
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
            <PageCenterLoader label="Загрузка отзывов" minHeight={160} />
          </Box>
        ) : !loading && rows.length === 0 ? (
          <EmptyState
            title="Нет отзывов"
            description="За выбранный период и фильтры записей нет."
            icon={<IconStar size={28} stroke={1.5} />}
          />
        ) : (
          <Card withBorder padding="sm" radius="md">
            <Stack gap="sm">
              {total > 0 ? (
                <Group justify="space-between" align="center" wrap="wrap" gap="sm">
                  <Group gap="xs" wrap="wrap" align="center">
                    <Text size="xs" c="dimmed">
                      Показывать:
                    </Text>
                    <Select
                      size="xs"
                      data={PAGE_SIZE_OPTIONS.map((n) => ({ value: String(n), label: String(n) }))}
                      value={String(pageSize)}
                      onChange={(value) => onPageSizeChange(value || String(PAGE_SIZE_OPTIONS[0]))}
                      allowDeselect={false}
                      checkIconPosition="right"
                      styles={{ root: { width: 80 } }}
                    />
                    <Text size="xs" c="dimmed">
                      записей
                    </Text>
                  </Group>
                  <Group gap="md" wrap="wrap" align="center">
                    <Text size="xs" c="dimmed">
                      Всего: <Text component="span" fw={600}>{total}</Text>
                    </Text>
                    {pages > 1 ? (
                      <PaginationBar
                        page={page}
                        pageCount={pages}
                        total={total}
                        disabled={loading}
                        onPageChange={(p) => setPage(p)}
                      />
                    ) : null}
                  </Group>
                </Group>
              ) : null}

              <DataTableScroll>
                <DataTable className="reviews-list-table">
                  <DataThead>
                    <DataTr>
                      <DataTh style={{ width: 88 }}>Обращение</DataTh>
                      <DataTh style={{ width: 120 }}>Проект</DataTh>
                      <DataTh style={{ width: 120 }}>Клиент</DataTh>
                      <DataTh style={{ width: 88 }}>Оценка</DataTh>
                      <DataTh style={{ minWidth: 200 }}>Что улучшить</DataTh>
                      <DataTh style={{ width: 100 }}>Кто запросил</DataTh>
                      <DataTh style={{ width: 120 }}>Оператор</DataTh>
                      <DataTh className="reviews-th--submitted" variantNarrow>
                        Дата
                      </DataTh>
                    </DataTr>
                  </DataThead>
                  <DataTbody>
                    {rows.map((r) => {
                      const num =
                        r.ticket_number != null && Number.isFinite(r.ticket_number)
                          ? r.ticket_number
                          : (r.ticket_id || '').slice(0, 8)
                      const improvement = (r.improvement_comment || '').trim() || '—'
                      return (
                        <DataTr
                          key={r.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openTicket(r.ticket_id)}
                        >
                          <DataTd>
                            <Text
                              component="span"
                              size="sm"
                              c="blue"
                              fw={500}
                              style={{ textDecoration: 'underline' }}
                              onClick={(e) => {
                                e.stopPropagation()
                                openTicket(r.ticket_id)
                              }}
                            >
                              #{num}
                            </Text>
                          </DataTd>
                          <DataTd clip>{r.project_name?.trim() || '—'}</DataTd>
                          <DataTd clip>{r.customer_name?.trim() || '—'}</DataTd>
                          <DataTd>{ratingCellText(r)}</DataTd>
                          <DataTd
                            style={{
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxWidth: 480,
                            }}
                          >
                            {improvement}
                          </DataTd>
                          <DataTd clip>{requestedByLabel(r)}</DataTd>
                          <DataTd clip>{r.assigned_operator_name?.trim() || '—'}</DataTd>
                          <DataTd
                            muted
                            className="reviews-td--submitted"
                            title={r.submitted_at ? formatWhen(r.submitted_at) : undefined}
                          >
                            <Text
                              fz={10}
                              lh={1.2}
                              className={dataTableClass.mono}
                              c="dimmed"
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {formatCreatedAtTableCell(r.submitted_at)}
                            </Text>
                          </DataTd>
                        </DataTr>
                      )
                    })}
                  </DataTbody>
                </DataTable>
              </DataTableScroll>

              {total > 0 && pages > 1 ? (
                <Group justify="flex-end" wrap="wrap">
                  <PaginationBar
                    page={page}
                    pageCount={pages}
                    total={total}
                    disabled={loading}
                    onPageChange={(p) => setPage(p)}
                  />
                </Group>
              ) : null}
            </Stack>
          </Card>
        )}
      </Stack>
    </PageShell>
  )
}
